import useSWR from 'swr';
import { useState } from 'react';

// Types
import type { AxiosError } from 'axios';
import type { SWRConfiguration, KeyedMutator } from 'swr';
import type { ReportsType, ReportType } from '@/utils/hooks/swr/reports/types';
import type { SocialProfileType } from '@/types/socialProfileType';
import type { SpGroupType } from '@/utils/hooks/swr/group/useSPGroups';

// Utils
import { get } from '@/utils/client';
import { REPORTS, REPORTS_SCHEDULED } from '@/lib/routes/apiEndpoints';

// Hooks
import useSocialProfiles from '@/utils/hooks/swr/useSocialProfiles';

export type FiltersReportType =
    | 'hashtags'
    | 'competitors'
    | 'dashboards'
    | 'accounts'
    | 'yearly'
    | 'monthly'
    | 'weekly'
    | 'daily'
    | 'quarterly';

export type RequestParamsType = {
    per_page: number;
    page: number;
    sort?: {
        column: string;
        order: 'asc' | 'desc';
    };
    filter_social_profile?: SocialProfileType['id'];
    filter_report?: FiltersReportType;
    filter_group?: SpGroupType['id'];
};

export type SetRequestParamsType = ReturnType<typeof useFetchReports>['setRequestParams'];

type useFetchReportsProps = {
    options?: SWRConfiguration<ReportsType, AxiosError<Error>>;
    params?: RequestParamsType;
    isScheduled?: boolean;
};

export type MutateReports = KeyedMutator<ReportsType>;

const useFetchReports = (
    { options, params, isScheduled }: useFetchReportsProps = {
        options: {},
        params: null,
        isScheduled: false
    }
) => {
    const [requestParams, setRequestParams] = useState<RequestParamsType>(params);
    const { socialProfiles } = useSocialProfiles({
        revalidateIfStale: false,
        shouldRetryOnError: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    const {
        data: reports,
        mutate: mutateReports,
        error: errorReports,
        isLoading
    } = useSWR<ReportsType, AxiosError<Error>>(
        [isScheduled ? REPORTS_SCHEDULED : REPORTS, JSON.stringify(requestParams)],
        ([url]) =>
            get(url, requestParams).then((res) => {
                return {
                    ...res?.data,
                    data: res?.data?.data?.map((report) => {
                        return {
                            ...report,
                            format:
                                report?.params?.type?.includes('pdf') ||
                                report?.type?.includes('pdf')
                                    ? 'pdf'
                                    : 'xls',
                            type: isScheduled
                                ? report?.type?.split('_')[0]
                                : report?.params?.type?.split('_')?.[0],
                            start_date: report?.params?.start_date as string,
                            end_date: report?.params?.end_date as string
                        } as ReportType;
                    })
                };
            }),
        {
            shouldRetryOnError: false,
            ...options
        }
    );

    const formattedDataWithDashboard = () => {
        if (reports) {
            return {
                ...reports,
                data: reports?.data?.map((el) => {
                    if (!('dashboards' in el) || !el?.dashboards?.length) {
                        return el;
                    }
                    const dashboardIds = el?.dashboards[0]?.data?.social_profiles ?? [];

                    return {
                        ...el,
                        social_profiles: socialProfiles?.filter(
                            (sp) => dashboardIds?.includes(sp.id)
                        )
                    };
                })
            };
        }

        return null;
    };

    return {
        reports: formattedDataWithDashboard(),
        mutateReports,
        errorReports,
        requestParams,
        setRequestParams,
        isLoading
    };
};

export default useFetchReports;
